import type { SceneGroup } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

import { ApiService } from 'js/src/client';
import { getCaptionsCookie } from 'js/src/data/media/cookie-operations';
import { getCaptionsSceneDesignsForTemplate } from 'js/src/data/templates/templates-selectors';
import { setObjectPropertyValue } from 'js/src/editor/util';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getAccentColorsList } from 'js/src/luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

interface BlueprintCaptionSettings {
    visible: boolean;
    sceneDesignId?: string;
    fontUrl?: string;
    fillColor?: number;
    textSize?: number;
    accentColors?: Array<{
        assetName: string;
        path: string;
        color: number | string;
    }>;
}

const fetchBlueprintCaptionSettings = async (
    blueprintId: number,
    templateId: string,
): Promise<BlueprintCaptionSettings | null> => {
    try {
        // Fetch template style video data from blueprint
        const response = await ApiService.blueprintsTemplateStyleVideoDataRetrieve({
            path: { id: blueprintId },
            query: { template: templateId },
        });

        // Extract caption settings
        const dataLuminary = new Luminary({
            fetchResources: false,
            disableRenderer: true,
            autoDraw: false,
            autoUpdate: false,
        });
        dataLuminary.load(JSON.parse(response.data.video_data));

        const sceneGroupWithCaptions = dataLuminary.sceneGroups.find((sg) => sg.captions);
        if (sceneGroupWithCaptions) {
            const captionAsset = sceneGroupWithCaptions.captions.asset;

            // Extract accent colors from the caption asset
            let accentColors: Array<{ assetName: string; path: string; color: number | string }> =
                [];
            if (captionAsset) {
                // Get accent colors using the same logic as the CaptionColorSelector component
                const accentColorsList = getAccentColorsList(captionAsset);
                accentColors = accentColorsList.map((accentColor) => ({
                    assetName: accentColor.assetName,
                    path: accentColor.path,
                    color: accentColor.color,
                }));
            }

            return {
                visible: sceneGroupWithCaptions.captions.visible,
                sceneDesignId: sceneGroupWithCaptions.captions.sceneDesignId ?? undefined,
                fontUrl: captionAsset?.normalStyle?.fontUrl,
                fillColor: captionAsset?.normalStyle?.fill,
                textSize: captionAsset?.textSize,
                accentColors,
            };
        }

        return null;
    } catch (error) {
        ErrorReporter.capture(error);
        return null;
    }
};

/**
 * Sets caption enabled (and style) on a scene group from blueprint settings or cookies
 * If blueprint is available, it takes priority over cookies.
 * Cookies is only used to turn captions on or off (not styling).
 */
export const setCaptionsFromBlueprintOrCookiesOperation = createLuminaryOperation(
    async (
        { getState },
        sceneGroup: SceneGroup,
        blueprintId: number | null,
        templateId: string,
        defaultSceneDesignId: string | null,
    ): Promise<void> => {
        // Set default scene design first, if not already set
        const captionsSceneDesignIdOptions = getCaptionsSceneDesignsForTemplate(
            getState(),
            templateId,
        ).map((sceneDesign) => sceneDesign.get('id'));
        const existingSceneDesignId = sceneGroup.captions.sceneDesignId;

        if (
            !existingSceneDesignId ||
            !captionsSceneDesignIdOptions.includes(existingSceneDesignId)
        ) {
            sceneGroup.captions.sceneDesignId = defaultSceneDesignId;
        }

        // Try blueprint first
        if (blueprintId && templateId) {
            try {
                const blueprintSettings = await fetchBlueprintCaptionSettings(
                    blueprintId,
                    templateId,
                );
                if (blueprintSettings) {
                    if (blueprintSettings.sceneDesignId) {
                        sceneGroup.captions.sceneDesignId = blueprintSettings.sceneDesignId;
                    }

                    // Apply styling properties to caption assets
                    const captionAsset = sceneGroup.captions.asset;
                    if (captionAsset) {
                        if (blueprintSettings.fontUrl) {
                            setObjectPropertyValue(
                                captionAsset,
                                'normalStyle.fontUrl',
                                blueprintSettings.fontUrl,
                            );
                        }

                        if (blueprintSettings.fillColor) {
                            setObjectPropertyValue(
                                captionAsset,
                                'normalStyle.fill',
                                blueprintSettings.fillColor,
                            );
                        }

                        if (blueprintSettings.textSize) {
                            captionAsset.textSize = blueprintSettings.textSize;
                        }
                    }

                    // Apply accent colors to caption assets
                    if (
                        blueprintSettings.accentColors &&
                        blueprintSettings.accentColors.length > 0
                    ) {
                        blueprintSettings.accentColors.forEach((accentColor) => {
                            // Find the asset by name and apply the color to the specified path
                            const targetAsset = sceneGroup.captions.getAssetByName?.(
                                accentColor.assetName,
                            );
                            if (targetAsset) {
                                setObjectPropertyValue(
                                    targetAsset,
                                    accentColor.path,
                                    accentColor.color,
                                );
                            }
                        });
                    }

                    sceneGroup.captions.visibleWithUpdate = blueprintSettings.visible;

                    return;
                }
            } catch (error) {
                // Error fetching blueprint styling settings, keep default
            }
        }

        // fallback to cookies
        sceneGroup.captions.visibleWithUpdate = getCaptionsCookie() ?? false;
    },
);
